import { IconDownload } from '@tabler/icons-react';
import {
  ColDef,
  ICellRendererParams,
  RowSelectionOptions,
  SelectionColumnDef,
  SortChangedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';

import { Box, Button, Typography } from '@mui/material';

import { ContractSummary } from '@octopus/api';
import { formatDateBR, formatMoney } from '@octopus/formatters';
import { SimulationSummary } from '@octopus/termination-api';
import { DataGridProps, adaptGridColDefs } from '@octopus/ui/data-grid';
import { CurrencyEditor } from '@octopus/ui/shared-components';

import { departmentColumn } from '../../../../modules/components/table/columns/CompanyColumns';
import { workerNameAndPictureColumn } from '../../../../modules/components/table/columns/WorkerColumns';
import { terminationReason, typeOfNotice } from '../../constants';
import { ContractSummaryWithFgtsBalance } from '../../types';
import { formatTimeSinceAdmission } from '../../utils';

export const getColumns = ({
  onSelectContract,
  activeTab,
}: {
  onSelectContract: (
    params: ICellRendererParams<ContractSummaryWithFgtsBalance>,
  ) => void;
  activeTab: string;
}): ColDef[] => {
  if (activeTab === 'collaborators') {
    return contractColumns({ onSelectContract });
  }

  return simulationColumns;
};

const simulationColumns = [
  ...adaptGridColDefs([workerNameAndPictureColumn()]),
  {
    headerName: 'Data de desligamento',
    field: 'terminationDate',
    sortable: false,
    valueFormatter: (params: ValueFormatterParams<SimulationSummary>) => {
      return formatDateBR(params.data?.terminationDate);
    },
  },
  {
    headerName: 'Tipo de aviso',
    field: 'noticeType',
    sortable: false,
    valueFormatter: (params: ValueFormatterParams<SimulationSummary>) => {
      return typeOfNotice?.[params.data?.br.comoLidarComAvisoPrevioNaoCumprido];
    },
  },
  {
    headerName: 'Motivo',
    field: 'reason',
    sortable: false,
    valueFormatter: (params: ValueFormatterParams<SimulationSummary>) => {
      return terminationReason?.[params.data?.br.motivo];
    },
  },
  {
    headerName: 'Custo Total',
    field: 'totalCost',
    sortable: false,
    valueFormatter: (params: ValueFormatterParams<SimulationSummary>) => {
      return formatMoney(params.data?.totalizers?.totals.totalCost);
    },
  },
  {
    headerName: '',
    field: 'action',
    sortable: false,
    maxWidth: 100,
    cellRenderer: (_: ICellRendererParams<SimulationSummary>) => {
      return (
        <Button
          color="secondary"
          variant="outlined"
          sx={{ padding: 1, minWidth: 0 }}
        >
          <IconDownload size={16} />
        </Button>
      );
    },
  },
];

const contractColumns = ({
  onSelectContract,
}: {
  onSelectContract: (
    params: ICellRendererParams<ContractSummaryWithFgtsBalance>,
  ) => void;
}) => [
  ...adaptGridColDefs([workerNameAndPictureColumn(), departmentColumn()]),
  admissionDateColumn,
  {
    headerName: 'Ação',
    field: 'action',
    sortable: false,
    cellRenderer: (
      params: ICellRendererParams<ContractSummaryWithFgtsBalance>,
    ) => {
      return (
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onSelectContract(params);
          }}
        >
          <Typography p={0.5} variant="body2">
            Simular rescisão
          </Typography>
        </Button>
      );
    },
  },
];

export const getFormColumns = (): ColDef[] => [
  ...adaptGridColDefs([workerNameAndPictureColumn()]),
  admissionDateColumn,
  {
    headerName: 'Saldo FGTS',
    field: 'fgtsBalance',
    cellEditor: CurrencyEditor,
    cellRenderer: (
      params: ICellRendererParams<ContractSummaryWithFgtsBalance>,
    ) => {
      const color = params.data?.fgtsBalance
        ? 'text.secondary'
        : 'text.placeholder';
      return (
        <Box>
          <Typography variant="body2" color={color}>
            {params.data?.fgtsBalance !== undefined
              ? formatMoney(params.data?.fgtsBalance)
              : '-'}
          </Typography>
        </Box>
      );
    },
    editable: true,
  },
];

const admissionDateColumn: ColDef = {
  headerName: 'Tempo de casa',
  sortable: true,
  field: 'admissionDate',
  valueFormatter: (params: ValueFormatterParams<ContractSummary>) => {
    return formatTimeSinceAdmission(params.data?.admissionDate);
  },
};

export const handleSortChanged =
  (dataGridProps: DataGridProps) => (event: SortChangedEvent) => {
    const column = event.columns?.[0];
    if (column) {
      dataGridProps.sortingProps.setField(column.getColId());
      dataGridProps.sortingProps.setOrder(column.getSort());
    } else {
      dataGridProps.sortingProps.setField(undefined);
      dataGridProps.sortingProps.setOrder(undefined);
    }
  };

export const defaultColDef: ColDef = {
  autoHeight: false,
  filter: false,
  sortable: false,
  suppressHeaderMenuButton: true,
  suppressHeaderFilterButton: true,
  suppressHeaderContextMenu: true,
  resizable: false,
  rowDrag: false,
  cellStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    cursor: 'pointer',
  },
  flex: 1,
  cellRenderer: (params: ICellRendererParams) => {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {params.valueFormatted}
      </Typography>
    );
  },
  tooltipValueGetter: (p) => p.valueFormatted ?? p.value, // texto do tooltip
  tooltipComponent: 'TruncatedTooltip', // componente React
  tooltipComponentParams: { maxWidth: 420 }, // se precisar de props
  comparator: () => 0,
};

export const rowSelection: RowSelectionOptions = {
  mode: 'multiRow',
  enableClickSelection: false,
};

export const selectionColumnDef: SelectionColumnDef = {
  pinned: 'left',
  lockPinned: true,
};
